import { useLazyGetResidentsIndexQuery } from "app/services/api/residentsApi";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";
import { useState, useCallback, useMemo, useEffect } from "react";
import { getFullName } from "ui/utils/nameUtil";

interface Props {
  selectedLocationId?: number;
  preselectedResidents?: ResidentModel[];
}
export function useHandleAllResidents({
  selectedLocationId,
  preselectedResidents,
}: Props) {
  const [residents, setResidents] = useState<ResidentModel[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [fetch, { isLoading: areResidentsLoading }] =
    useLazyGetResidentsIndexQuery();

  // Function to search residents by name
  const searchResidents = useCallback(
    async (search: string) => {
      if (selectedLocationId === undefined) {
        setResidents([]);
        return;
      }

      setSearchTerm(search);

      try {
        const result = await fetch({
          filter: {
            location_id: selectedLocationId,
            search: search || undefined,
          },
          page: 1,
        }).unwrap();

        setResidents(result.data ?? []);
      } catch (error) {
        setResidents([]);
      }
    },
    [selectedLocationId, fetch],
  );

  // Load initial residents when location changes
  useEffect(() => {
    if (selectedLocationId === undefined) {
      setResidents([]);
      setSearchTerm("");
      return;
    }

    // Load initial list by calling searchResidents with empty string
    searchResidents("");
  }, [selectedLocationId, searchResidents]);

  const userOptions = useMemo(() => {
    const residentsList = residents || [];
    let combinedResidents: ResidentModel[] = residentsList;

    // If there are preselected residents, merge them with the list
    if (preselectedResidents?.length) {
      // Create a Set of existing resident IDs for efficient lookup
      const existingIds = new Set(residentsList.map((r) => r.id));

      // Add preselected residents that aren't already in the list
      const uniquePreselected = preselectedResidents.filter(
        (resident) =>
          (!selectedLocationId ||
            selectedLocationId === resident.location?.id) &&
          !existingIds.has(resident.id),
      );

      // Combine the lists: preselected first, then fetched residents
      combinedResidents = [...uniquePreselected, ...residentsList];
    }

    return combinedResidents.map((resident: ResidentModel) => ({
      value: resident.id,
      label: getFullName(resident),
    }));
  }, [residents, preselectedResidents, selectedLocationId]);

  return {
    userOptions,
    areResidentsLoading,
    residents,
    searchResidents,
    searchTerm,
  };
}
